import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { getTheme } from './theme';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeModeContext';
import Layout from './components/Layout';
import AppRoutes from './routes/AppRoutes';
import { isAbortError } from './lib/supabase';
import { SupportHubProvider } from './features/support-hub/SupportHubProvider';
import { OnboardingManager } from './components/OnboardingManager';
import { useMemo } from 'react';


// Create a client instance for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error) => {
        // Never retry AbortErrors – the request was intentionally cancelled
        if (isAbortError(error)) return false;
        return failureCount < 1;
      },
      // Prevent AbortErrors from bubbling to error boundaries
      throwOnError: (error) => {
        if (isAbortError(error)) return false;
        return false; // keep default behaviour (don't throw for any query error)
      },
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: false,
    },
  },
});

function ThemedApp() {
  const { mode } = useThemeMode();
  const theme = useMemo(() => getTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <SupportHubProvider>
            <Layout>
              {/* Onboarding wizard - auto-shows based on user's onboarding_status */}
              <OnboardingManager />

              {/* Main Application Routes */}
              <AppRoutes />

            </Layout>
          </SupportHubProvider>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        autoHideDuration={6000}
        style={{ zIndex: 9999 }}
      >
        <ThemeModeProvider>
          <ThemedApp />
        </ThemeModeProvider>
      </SnackbarProvider>
    </QueryClientProvider>
  );
}

export default App;
